/**
 * postAuthRedirect — single source of truth for "return to the page the user
 * was trying to reach" across every authentication path (email login, Google/
 * Facebook social login, expired-session re-auth, sign-up + onboarding, and
 * IDV / pending gates).
 *
 * Why this exists:
 *   A tapped SMS/job deep link (e.g. /talent/gigs/{id}) must survive auth.
 *   The `?redirect=` query param alone is lost the moment the flow becomes
 *   social login (external OAuth round-trip), sign-up, or a gate bounce. We
 *   therefore also persist the destination in sessionStorage so ANY path can
 *   restore it.
 *
 * Security: only same-origin, app-relative paths are ever accepted — never an
 * absolute/protocol-relative URL — so this cannot become an open redirect.
 */

const KEY = 'post_auth_redirect';

// Routes that are never valid *return* destinations (would loop or be useless).
const NON_RETURNABLE_PREFIXES = ['/login', '/register', '/auth/callback', '/forgot-password'];

/**
 * True only for a safe, app-internal path we can navigate to after auth.
 * Rejects absolute URLs, protocol-relative (`//evil.com`), backslash tricks,
 * and control characters.
 */
export function isSafeInternalPath(p) {
  if (typeof p !== 'string' || p.length === 0) return false;
  if (!p.startsWith('/')) return false;          // must be app-relative
  if (p.startsWith('//') || p.startsWith('/\\')) return false; // protocol-relative / UNC
  if (p.includes('://')) return false;            // embedded absolute URL
  if (p.includes('\\')) return false;             // backslash escape trick
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001F]/.test(p)) return false;    // control chars
  return true;
}

function isReturnable(p) {
  if (!isSafeInternalPath(p)) return false;
  return !NON_RETURNABLE_PREFIXES.some((pre) => p === pre || p.startsWith(pre + '?') || p.startsWith(pre + '/'));
}

/** Persist the intended destination (no-op for unsafe / non-returnable paths). */
export function saveIntendedRoute(path) {
  try {
    if (isReturnable(path)) sessionStorage.setItem(KEY, path);
  } catch (_e) { /* sessionStorage unavailable */ }
}

/** Read the stored destination without clearing it (null if none/unsafe). */
export function peekIntendedRoute() {
  try {
    const v = sessionStorage.getItem(KEY);
    return isReturnable(v) ? v : null;
  } catch (_e) { return null; }
}

/** Read AND clear the stored destination (null if none/unsafe). */
export function consumeIntendedRoute() {
  const v = peekIntendedRoute();
  try { sessionStorage.removeItem(KEY); } catch (_e) { /* ignore */ }
  return v;
}

/**
 * Resolve the best post-auth destination: prefer an explicit, validated
 * `?redirect=` query param, else the stored intended route. Returns null when
 * neither is a safe internal path (caller then falls back to a role home).
 */
export function resolveRedirect(rawQueryRedirect) {
  if (isReturnable(rawQueryRedirect)) return rawQueryRedirect;
  return peekIntendedRoute();
}
